import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Select, message, Modal, Button, Space, Divider } from "antd";
import { getRequest, postRequest, deleteRequest } from "../../util/service";
import { fileNameError, isValidFileName } from "../../util/fileNameValidation";
import { DeleteOutlined, ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";

const { Option, OptGroup } = Select;

export default function FileUploadField({
  projectId,
  fileType,
  value,
  onChange,
  accept,
  maxSizeMB,
  predefOptions = [],
  onUploadStateChange = () => {},
  disabled = false,
  optionGroups,
  onReload, // Optional callback to refresh parent component data
  loading: externalLoading,
}) {
  const [userFiles, setUserFiles]     = useState([]);
  const [commonFiles, setCommonFiles] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const inputRef = useRef();
  const allowedExtensions = String(accept || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item.startsWith("."));
  const allowedFormatsText = allowedExtensions.join(", ");

  const labelFor = (fname) =>
    predefOptions.find((p) => p.value === fname)?.label || fname;

  const fetchFiles = () => {
    // Only fetch if this component is managing its own data (i.e., not for blocks_file)
    if (!projectId || optionGroups) return;
    setInternalLoading(true);
    getRequest(`/riva/moleculegenbatches/${projectId}/${fileType}`)
      .then((resp) => {
        const data       = resp.data || [];
        const filesData  = data.find((d) => d.files)?.files        || [];
        const commonData = data.find((d) => d.common_files)?.common_files || [];
        setUserFiles(filesData.filter(Boolean));
        setCommonFiles(commonData.filter(Boolean));
      })
      .catch(() => message.error(`Failed to load ${fileType}`))
      .finally(() => setInternalLoading(false));
  };

  useEffect(fetchFiles, [projectId, fileType, optionGroups]);

  useEffect(() => {
    if (value || fileType !== "Smartsfiles") return;
    const defaultSmartsFile = commonFiles.find((name) => name === "SMARTS.txt");
    if (defaultSmartsFile) {
      onChange(defaultSmartsFile);
    }
  }, [commonFiles, fileType, onChange, value]);

  const handleSelect = (val) => {
    if (val === "__UPLOAD_NEW__") {
      if (inputRef.current) inputRef.current.click();
    } else {
      onChange(val);
    }
  };

  const handleDelete = (fname, e) => {
    e.stopPropagation();
    Modal.confirm({
      title: `Delete "${fname}"?`,
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      onOk: async () => {
        try {
          await deleteRequest(
            `/riva/moleculegenbatches/${projectId}/${fileType}/${fname}`
          );
          message.error(`Deleted "${fname}"`);
          if (value === fname) onChange(null);
          // Re-fetch files for both grouped and non-grouped scenarios
          if(typeof onReload === "function") onReload(); // Prop from parent if exists
          else fetchFiles();

        } catch (err) {
          message.error(`Delete failed: ${err.message}`);
        }
      },
    });
  };

  const handlePicked = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const lowerName = String(file.name || "").toLowerCase();
    if (!isValidFileName(file.name)) {
      message.error(fileNameError(file.name));
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (allowedExtensions.length && !allowedExtensions.some((ext) => lowerName.endsWith(ext))) {
      message.warning(`Only ${allowedFormatsText} files are allowed.`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // Check against all possible files to prevent duplicates
    const allFiles = optionGroups 
        ? optionGroups.flatMap(g => g.options.map(o => o.value)) 
        : [...userFiles, ...commonFiles];

    if (allFiles.includes(file.name)) {
      return message.error(`"${file.name}" already exists – choose it from the list.`);
    }
    onUploadStateChange(true);
    try {
      const resp = await postRequest(
        `/riva/moleculegenbatches/${projectId}/presigned-url`,
        { file_type: fileType, file_name: file.name, action: "upload" }
      );
      const { url } = resp.data || {};
      if (!url) throw new Error("No presigned URL returned");
      await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      message.success(`Uploaded "${file.name}"`);
      onChange(file.name);
      if (onReload) onReload();
      else fetchFiles();
    } catch (err) {
      message.error(`Upload failed: ${err.message}`);
    } finally {
      onUploadStateChange(false);
      if (inputRef.current) inputRef.current.value = ""; // Reset file input
    }
  };
  const isLoading = externalLoading || internalLoading;

  return (
    <>
      <Select
        disabled={disabled}
        loading={isLoading}
        style={{ width: "100%" }}
        placeholder="Select or upload…"
        value={value}
        onChange={handleSelect}
        onOpenChange={(open) => {
          if (open && typeof onReload === "function") {
            onReload();
          }
        }}
        listHeight={256}
        dropdownRender={(menu) => (
          <>
                {menu}
            {!disabled && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ padding: "0 8px 8px", textAlign: "center" }}>
                  <Button
                    icon={<UploadOutlined />}
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => inputRef.current?.click()}
                  >
                    Upload new file…
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      >
        {optionGroups ? (
          optionGroups.map(group => (
            <OptGroup key={group.label} label={group.label}>
              {group.options.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    {opt.label}
                    {opt.deletable && !disabled && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => handleDelete(opt.value, e)}
                        size="small"
                      />
                    )}
                  </Space>
                </Option>
              ))}
            </OptGroup>
          ))
        ) : (
          <>
            {userFiles.map((fname) => (
              <Option key={fname} value={fname}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  {labelFor(fname)}
                  {!disabled && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => handleDelete(fname, e)}
                      size="small"
                    />
                  )}
                </Space>
              </Option>
            ))}
            {commonFiles.map((fname) =>
              fname ? (
                <Option key={fname} value={fname}>
                  {labelFor(fname)}
                </Option>
              ) : null
            )}
          </>
        )}
      </Select>
      <input
        type="file"
        accept={accept}
        ref={inputRef}
        style={{ display: "none" }}
        onChange={handlePicked}
      />
    </>
  );
}

FileUploadField.propTypes = {
    projectId: PropTypes.string,
    fileType: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    accept: PropTypes.string,
    maxSizeMB: PropTypes.number,
    onUploadStateChange: PropTypes.func,
    disabled: PropTypes.bool,
    optionGroups: PropTypes.array,
    loading: PropTypes.bool,
    predefOptions: PropTypes.array,
     onReload: PropTypes.func,
};
